import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { CredentialData, CredentialRevealedData } from '@/types/api';

const KEY = (clientId: number) => ['credentials', clientId];

export function useCredentials(clientId: number | undefined) {
  return useQuery({
    queryKey: clientId == null ? ['credentials', null] : KEY(clientId),
    enabled: clientId != null,
    queryFn: async (): Promise<CredentialData[]> => {
      const { data } = await api.get<CredentialData[]>(`/v1/clients/${clientId}/credentials`);
      return data;
    },
  });
}

export type CredentialInput = {
  type: string;
  label: string;
  url?: string | null;
  username?: string | null;
  value: string;
  notes?: string | null;
};

export function useCreateCredential(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CredentialInput): Promise<CredentialData> => {
      await primeCsrf();
      const { data } = await api.post<CredentialData>(
        `/v1/clients/${clientId}/credentials`,
        input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(clientId) }),
  });
}

export function useUpdateCredential(clientId: number, credentialId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<CredentialInput>): Promise<CredentialData> => {
      await primeCsrf();
      const { data } = await api.patch<CredentialData>(
        `/v1/clients/${clientId}/credentials/${credentialId}`,
        patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(clientId) }),
  });
}

export function useDeleteCredential(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (credentialId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/credentials/${credentialId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(clientId) }),
  });
}

export function useRevealCredential(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (credentialId: number): Promise<CredentialRevealedData> => {
      await primeCsrf();
      const { data } = await api.post<CredentialRevealedData>(
        `/v1/clients/${clientId}/credentials/${credentialId}/reveal`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(clientId) }),
  });
}
